import { Component, OnInit, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfigPair, ConfigPairList, TenantBusinessPlans, TenantConfigRes, TENANT_BUSINESS_PLANS } from 'app/components/models/tenant';
import { fadeInAnimation } from 'app/core/route-animation/route.animation';
import { CommonUtilService } from 'app/services/commonUtil.service';
import { ConfigDataService } from 'app/services/config.service';
import { SpinnerService } from 'app/services/spinner.service';
import { from, of } from 'rxjs';
import { catchError, concatMap, map, reduce, retry } from 'rxjs/operators';

@Component({
  selector: 'ms-manage-config',
  templateUrl: './manage-config.component.html',
  styleUrls: ['./manage-config.component.css'],
  encapsulation: ViewEncapsulation.None,
  host: {
    "[@fadeInAnimation]": 'true'
  },
  animations: [fadeInAnimation]
})
export class ManageConfigComponent implements OnInit {

  @ViewChild('emailActionCellTpl') emailActionCellTpl: TemplateRef<any>;

  submitted = false;
  error = false;
  success = false;
  dataJSON: any;
  data: any;
  tenantId: string;
  configForm: FormGroup;
  planList: TenantBusinessPlans[] = [];
  dnsExists: boolean = false;
  tenantExists: boolean = false;
  configList: TenantConfigRes[] = [];
  emailRows = [];
  emailColumns = [];
  publicRows = [];
  publicColumns = [];
  privateRows = [];
  privateColumns = [];
  editingEmail = {};
  editingPublic = {};
  editingPrivate = {};
  emptyKeyPair: any;
  controlsEmail: FormArray;
  controlsPublic: FormArray;
  controlsPrivate: FormArray;

  constructor(
    private formBuilder: FormBuilder,
    private commonUtilService: CommonUtilService,
    private configService: ConfigDataService,
    private router: Router,
    private activateRoute: ActivatedRoute,
    private spinnerService: SpinnerService) { }

  ngOnInit(): void {
    this.spinnerService.start();
    this.planList = TENANT_BUSINESS_PLANS;

    this.commonUtilService.translateLang("HRWE.PAGE_TITLE.TENANT_DETAIL");

    this.emptyKeyPair = {
      "key": "-",
      "val": "-"
    };

    this.configForm = this.createTenantFormBuilder(this.formBuilder);

    this.tenantId = this.activateRoute.snapshot.paramMap.get('id');

    this.getConfigData();

    this.getConfigList();

  }

  getConfigData(): void {

    this.spinnerService.start();

    this.configService.getTenant(this.tenantId).subscribe(data => {
      this.data = data;
      console.log(data);

      this.configForm = this.createTenantFormBuilder(this.formBuilder);

      if (this.data && this.data.emailConfig) {
        for (let key in this.data.emailConfig) {
          this.emailRows.push({
            "key": key,
            "val": this.data.emailConfig[key]
          });
          this.emailRows = [...this.emailRows];
        }
        //console.log(this.emailRows);
      }

      if (this.data && this.data.publicConfig) {
        for (let key in this.data.publicConfig) {
          this.publicRows.push({
            "key": key,
            "val": this.data.publicConfig[key]
          });
          this.publicRows = [...this.publicRows];
        }
      }

      if (this.data && this.data.privateConfig) {
        for (let key in this.data.privateConfig) {
          this.privateRows.push({
            "key": key,
            "val": this.data.privateConfig[key]
          });
          this.privateRows = [...this.privateRows];
        }
      }

      this.createFormForConfigTables();

      if (this.emailRows.length == 0) {
        this.emailRows = this.emailRows.concat(this.emptyKeyPair);
      }

      if (this.publicRows.length == 0) {
        this.publicRows = this.publicRows.concat(this.emptyKeyPair);
      }

      if (this.privateRows.length == 0) {
        this.privateRows = this.privateRows.concat(this.emptyKeyPair);
      }

      this.spinnerService.stop();

    });
  }

  addEmptyEmailConfig() {

    this.spinnerService.start();

    let index = this.emailRows.findIndex(row => row.key === '-');

    if (index == -1) {
      this.emailRows.push({
        "key": "-",
        "val": "-"
      });

      this.emailRows = [...this.emailRows];
    }
    this.spinnerService.stop();
  }

  addEmptyPublicConfig() {

    this.spinnerService.start();

    let index = this.publicRows.findIndex(row => row.key === '-');

    if (index == -1) {
      this.publicRows.push({
        "key": "-",
        "val": "-"
      });

      this.publicRows = [...this.publicRows];
    }
    this.spinnerService.stop();
  }

  addEmptyPrivateConfig() {

    this.spinnerService.start();

    let index = this.privateRows.findIndex(row => row.key === '-');

    if (index == -1) {
      this.privateRows.push({
        "key": "-",
        "val": "-"
      });

      this.privateRows = [...this.privateRows];
    }
    this.spinnerService.stop();
  }

  deleteEmailConfig(key: string) {

    this.spinnerService.start();

    let index = this.emailRows.findIndex(row => row.key === key);

    this.emailRows.splice(index, 1);

    this.emailRows = [...this.emailRows];

    if (this.emailRows.length === 0) {
      this.addEmptyEmailConfig();
    }

    this.spinnerService.stop();

  }

  deletePublicConfig(key: string) {

    this.spinnerService.start();

    let index = this.publicRows.findIndex(row => row.key === key);

    this.publicRows.splice(index, 1);

    this.publicRows = [...this.publicRows];

    if (this.publicRows.length === 0) {
      this.addEmptyPublicConfig();
    }

    this.spinnerService.stop();

  }

  deletePrivateConfig(key: string) {

    this.spinnerService.start();

    let index = this.privateRows.findIndex(row => row.key === key);

    this.privateRows.splice(index, 1);

    this.privateRows = [...this.privateRows];

    if (this.privateRows.length === 0) {
      this.addEmptyPrivateConfig();
    }

    this.spinnerService.stop();

  }

  updateEmailTable(event, cell, rowIndex) {
    this.spinnerService.start();

    if (event.target.value !== "") {
      this.editingEmail[rowIndex + '-' + cell] = false;
      this.emailRows[rowIndex][cell] = event.target.value;
      this.emailRows = [...this.emailRows];
    }

    this.spinnerService.stop();
  }

  updatePublicTable(event, cell, rowIndex) {
    this.spinnerService.start();

    if (event.target.value !== "") {
      this.editingPublic[rowIndex + '-' + cell] = false;
      this.publicRows[rowIndex][cell] = event.target.value;
      this.publicRows = [...this.publicRows];
    }

    this.spinnerService.stop();
  }

  updatePrivateTable(event, cell, rowIndex) {
    this.spinnerService.start();

    if (event.target.value !== "") {
      this.editingPrivate[rowIndex + '-' + cell] = false;
      this.privateRows[rowIndex][cell] = event.target.value;
      this.privateRows = [...this.privateRows];
    }

    this.spinnerService.stop();
  }

  createTenantFormBuilder(formBuilder: FormBuilder) {
    return formBuilder.group({
      name: [this.data && this.data.tenant ? this.data.tenant : '', [Validators.required]],
      dns: [this.data && this.data.dns ? this.data.dns : '', [Validators.required]],
      businessPlan: [this.data && this.data.businessPlan ? this.data.businessPlan : '', Validators.required]
    });
  }

  get f() { return this.configForm.controls; }

  get val() { return this.configForm.value; }

  goToTenantList() {
    this.router.navigate(['/tenant']);
  }

  getConfigList() {
    this.spinnerService.start();
    this.configService.getConfigList().subscribe(data => {
      this.configList = data;

      if (this.configList) {
        let index = this.configList.findIndex(d => d.name === this.tenantId);
        this.configList.splice(index, 1);
      }
      /* console.log(this.configList); */
    });
    this.spinnerService.stop();
  }

  dnsCheck(searchValue: string): void {
    this.spinnerService.start();
    this.dnsExists = false;
    this.configList.forEach((e, i) => {
      if (searchValue === e.dns) {
        this.dnsExists = true;
      }
    });
    this.spinnerService.stop();
  }

  tenantCheck(searchValue: string): void {
    this.spinnerService.start();
    this.tenantExists = false;
    this.configList.forEach((e, i) => {
      if (searchValue === e.name) {
        this.tenantExists = true;
      }
    });
    this.spinnerService.stop();
  }


  onSubmit() {

    this.spinnerService.start();
    this.submitted = true;
    this.success = false;
    this.error = false;

    if (this.configForm.invalid) {

      this.commonUtilService.findInvalidControls(this.configForm);
      this.error = true;
      this.spinnerService.stop();
      return;
    }

    let configReq = [];

    this.dataJSON = {
      businessPlan: this.val.businessPlan,
      dns: this.val.dns,
      id: this.data.id,
      tenantName: this.val.name,
    }

    configReq.push(this.configService.updateTenantBasic(this.dataJSON)
      .pipe(retry(3))
      .pipe(map(value => ({ type: 'update_tenant_basic', value: value })))
      .pipe(catchError(value => of({ type: 'update_tenant_basic', failed: true }))));


    let emailPairs: ConfigPair[] = [];
    this.emailRows.forEach((e) => {
      if (e.key !== '-') {
        emailPairs.push(new ConfigPair(e.key, e.val));
      }
    });
    let emailList = new ConfigPairList();
    emailList.configPairs = emailPairs;


    configReq.push(this.configService.addOrUpdateEmailConfigList(emailList, this.tenantId)
      .pipe(retry(3))
      .pipe(map(value => ({ type: 'update_tenant_email_config', value: value })))
      .pipe(catchError(value => of({ type: 'update_tenant_email_config', failed: true }))));


    let publicPairs: ConfigPair[] = [];
    this.publicRows.forEach((e) => {
      if (e.key !== '-') {
        publicPairs.push(new ConfigPair(e.key, e.val));
      }
    });
    let publicList = new ConfigPairList();
    publicList.configPairs = publicPairs;

    configReq.push(this.configService.addOrUpdatePublicConfigList(publicList, this.tenantId)
      .pipe(retry(3))
      .pipe(map(value => ({ type: 'update_tenant_public_config', value: value })))
      .pipe(catchError(value => of({ type: 'update_tenant_public_config', failed: true }))));

    let privatePairs: ConfigPair[] = [];
    this.privateRows.forEach((e) => {
      if (e.key !== '-') {
        privatePairs.push(new ConfigPair(e.key, e.val));
      }
    });
    let privateList = new ConfigPairList();
    privateList.configPairs = privatePairs;

    configReq.push(this.configService.addOrUpdatePrivateConfigList(privateList, this.tenantId)
      .pipe(retry(3))
      .pipe(map(value => ({ type: 'update_tenant_private_config', value: value })))
      .pipe(catchError(value => of({ type: 'update_tenant_private_config', failed: true }))));

    from(configReq).pipe(concatMap((req) => req.pipe()), reduce((acc, cur) => [...acc, cur], []),).subscribe(results => {
      console.log(results);
      for (let result of results) {
        if (result['type'] === 'update_tenant_basic') {
          if (result['failed']) {
            console.log(result['type'] + " failed");
            this.error = true;
          }
        } else if (result['type'] === 'update_tenant_email_config') {
          if (result['failed']) {
            console.log(result['type'] + " failed");
            this.error = true;
          }
        } else if (result['type'] === 'update_tenant_public_config') {
          if (result['failed']) {
            console.log(result['type'] + " failed");
            this.error = true;
          }
        } else if (result['type'] === 'update_tenant_private_config') {
          if (result['failed']) {
            console.log(result['type'] + " failed");
            this.error = true;
          }
        }
      }
      this.spinnerService.stop();
      if (!this.error) {
        this.createFormForConfigTables();
        this.success = true;
        setTimeout(() => { this.goToTenantList(); }, 2000);
      }
    });

  }

  createFormForConfigTables() {

    this.spinnerService.start();

    let toGroupsEmail: FormGroup[];

    toGroupsEmail = this.emailRows.map(entity => {
      return new FormGroup({
        key: new FormControl(entity.key, [Validators.required, Validators.pattern(new RegExp("\\S"))]),
        val: new FormControl(entity.val, Validators.required)
      });
    });

    this.controlsEmail = new FormArray(toGroupsEmail);

    let toGroupsPublic: FormGroup[];

    toGroupsPublic = this.publicRows.map(entity => {
      return new FormGroup({
        key: new FormControl(entity.key, [Validators.required, Validators.pattern(new RegExp("\\S"))]),
        val: new FormControl(entity.val, Validators.required)
      })
    });

    this.controlsPublic = new FormArray(toGroupsPublic);

    let toGroupsPrivate: FormGroup[];

    toGroupsPrivate = this.privateRows.map(entity => {
      return new FormGroup({
        key: new FormControl(entity.key, [Validators.required, Validators.pattern(new RegExp("\\S"))]),
        val: new FormControl(entity.val, Validators.required)
      })
    });

    this.controlsPrivate = new FormArray(toGroupsPrivate);

    this.spinnerService.stop();
  }
}